import { useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { ChevronDown, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@workspace/ui/components/ui/popover";
import type { MakeInventoryCount } from "@/lib/api";
import { cn } from "@/lib/utils";

export interface MakeFilterProps {
  makes?: MakeInventoryCount[];
  value?: string;
  onChange: (make: string | undefined) => void;
  loading?: boolean;
}

function rankByInventory(makes: MakeInventoryCount[]): MakeInventoryCount[] {
  return [...makes].sort(
    (a, b) => b.count - a.count || a.make.localeCompare(b.make),
  );
}

export function MakeFilter({ makes = [], value, onChange, loading = false }: MakeFilterProps) {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef<HTMLInputElement>(null);
  const label = t("search.filter_labels.make");
  const allMakesLabel = t("search.placeholders.all_makes");
  const searchLabel = t("search.make_search_placeholder", {
    defaultValue: "Search makes",
  });

  const rankedMakes = useMemo(() => {
    const inventory = new Map(makes.map((item) => [item.make, item]));
    // Keep a selected value available even if its inventory count is zero or
    // it has disappeared from the latest catalogue response.
    if (value && !inventory.has(value)) inventory.set(value, { make: value, count: 0 });
    return rankByInventory([...inventory.values()]);
  }, [makes, value]);

  const matchingMakes = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return rankedMakes;
    return rankedMakes.filter((item) => item.make.toLocaleLowerCase().includes(normalizedQuery));
  }, [query, rankedMakes]);
  const options = useMemo(
    () => matchingMakes.map(({ make }) => make),
    [matchingMakes],
  );

  const close = () => {
    setOpen(false);
    setQuery("");
    setActiveIndex(-1);
  };

  const choose = (make: string | undefined) => {
    onChange(make);
    close();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, options.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const active = options[activeIndex];
      if (activeIndex >= 0 && activeIndex < options.length) choose(active);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };

  return (
    <div>
      <label className="text-sm font-medium text-foreground">{label}</label>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setQuery("");
            setActiveIndex(-1);
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={label}
            aria-haspopup="listbox"
            aria-expanded={open}
            data-testid="button-make-filter"
            onClick={() => {
              if (!open) {
                setQuery("");
                setActiveIndex(-1);
              }
            }}
            className="mt-1.5 flex min-h-11 w-full items-center justify-between gap-2 rounded-xl border border-input bg-background px-3 py-2 text-left text-sm transition-colors hover:border-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <span className={cn("truncate", value ? "text-foreground" : "text-muted-foreground")}>
              {value || allMakesLabel}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-[var(--radix-popover-trigger-width)] min-w-64 p-0"
          data-testid="make-filter-popover"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            searchRef.current?.focus();
          }}
        >
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              role="combobox"
              aria-label={searchLabel}
              aria-autocomplete="list"
              aria-expanded="true"
              aria-controls={`${id}-options`}
              aria-activedescendant={activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
              autoComplete="off"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder={searchLabel}
              data-testid="input-search-makes"
              className="min-h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div
            id={`${id}-options`}
            role="listbox"
            aria-label={label}
            className="max-h-64 overflow-y-auto p-1"
          >
            {matchingMakes.map((item, index) => {
              const optionIndex = index;
              return (
                <div
                  key={item.make}
                  id={`${id}-option-${optionIndex}`}
                  role="option"
                  aria-selected={item.make === value}
                  data-testid={`option-make-${index}`}
                  onMouseEnter={() => setActiveIndex(optionIndex)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(item.make)}
                  className={cn(
                    "flex min-h-10 cursor-pointer items-center justify-between gap-4 rounded-md px-3 py-2 text-sm",
                    activeIndex === optionIndex ? "bg-muted" : "hover:bg-muted/70",
                  )}
                >
                  <span className="truncate">{item.make}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {item.count.toLocaleString()}
                  </span>
                </div>
              );
            })}
            {!loading && matchingMakes.length === 0 && (
              <p role="status" className="px-3 py-3 text-sm text-muted-foreground">
                {t("search.make_search_empty", { defaultValue: "No makes found" })}
              </p>
            )}
            {loading && matchingMakes.length === 0 && (
              <p role="status" className="px-3 py-3 text-sm text-muted-foreground">
                {t("common.loading", { defaultValue: "Loading" })}
              </p>
            )}
          </div>
          <div className="border-t border-border p-2">
            <button
              type="button"
              onClick={() => choose(undefined)}
              disabled={!value}
              data-testid="button-clear-make"
              className="min-h-10 w-full rounded-md px-3 py-2 text-left text-sm font-medium text-primary hover:bg-muted/70 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:hover:bg-transparent"
            >
              {allMakesLabel}
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default MakeFilter;